import { api } from '../../../services/api';
import {
  calculatePagination,
  formatValue,
  invalidPage,
  getStatsFromCache,
  orderParams,
  titleCase,
} from '../../base/common/utils';
import { dateService } from '../../../services/date';

export class ProductService {
  constructor() {
    this.Api = api;
    this.limit = 500; // The size of API responses, perPage is for the display
    this.paginationOptions = [500, 100, 50];

    this.page = null;
    this.indexStart = null;
    this.indexEnd = null;
    this.productsByMerch = {};
    this.cartDataByMerch = {};
    this.productTotals = {};
  }

  getProductTrends(pubId, merchIds, params, pageNum, perPage, dateRange, resetPages = false) {
    if (!this.productsByMerch[merchIds]) {
      this.productsByMerch[merchIds] = {
        '30d': {
          pages: {},
          last_search_term: null,
        },
        '24h': {
          pages: {},
          last_search_term: null,
        },
      };
    }

    if (resetPages) {
      this.productsByMerch[merchIds][dateRange].pages = {};
    }

    const stats = this.productsByMerch[merchIds][dateRange];
    const searchTerm = params.search_term;
    const totalProducts = stats ? stats.total_items : 0;
    [this.page, this.indexStart, this.indexEnd] = calculatePagination(pageNum, perPage, this.limit);

    if (invalidPage(this.page, totalProducts, this.limit)) {
      return Promise.resolve('The page requested does not exist');
    }

    params.page = this.page;
    params.per_page = this.limit;
    params.merch_ids = merchIds;
    params.date_range = dateRange;

    if (stats.pages[this.page] && searchTerm === stats.last_search_term) {
      return getStatsFromCache(stats, this.page, this.indexStart, this.indexEnd);
    }

    return this.queryProductTrends(pubId, params, dateRange);
  }

  queryProductTrends(pubId, params, dateRange) {
    if (params.search_term === '') params.search_term = null;

    const paramKeys = [
      'date_from',
      'date_to',
      'page',
      'per_page',
      'merch_ids',
      'search_term',
      'order_by',
      'direction',
      'product_type',
      'date_range',
    ];
    params = orderParams(params, paramKeys);

    const today = dateService.getCurrentDateTime();
    const dailyParams = {
      ...params,
      date_from: dateService.formatDate(dateService.offsetFromDate(today, -1, 'day')),
      date_to: dateService.formatDate(today),
    };

    const endpoint = `/api/v0/publishers/${pubId}/products/`;
    const queryParams = dateRange === '30d' ? params : dailyParams;

    return this.Api.get(endpoint, queryParams).then(
      resp => this.onProductsLoaded(resp, params, dateRange),
      // TODO: add error modal () => onError(),
    );
  }

  onProductsLoaded(resp, params, dateRange) {
    const merchIds = params.merch_ids;

    const data = resp.data.data.length ? resp.data.data[0] : [];
    this.productsByMerch[merchIds][dateRange].pages[this.page] = data.stats;

    Object.assign(this.productsByMerch[merchIds][dateRange], {
      total_items: data.page_info.total_items,
      stats_summary: data.page_info.stats_summary,
      last_search_term: params.search_term || null,
    });

    return getStatsFromCache(
      this.productsByMerch[merchIds][dateRange],
      this.page,
      this.indexStart,
      this.indexEnd,
    );
  }

  queryCartData(pubId, params) {
    const merchId = params.merch_id;
    const productName = params.product_name;
    const currentDateRange = params.date_range;

    if (!this.cartDataByMerch[merchId]) {
      this.cartDataByMerch[merchId] = {};
    }

    if (!this.cartDataByMerch[merchId][productName]) {
      this.cartDataByMerch[merchId][productName] = {};
    }

    if (this.cartDataByMerch[merchId][productName][currentDateRange]) {
      return Promise.resolve(this.cartDataByMerch[merchId][productName][currentDateRange]);
    }

    const endpoint = `/api/v0/publishers/${pubId}/cart_siblings/`;
    return this.Api.get(endpoint, params).then(resp => {
      const data = resp.data.data.length ? resp.data.data[0] : [];
      this.cartDataByMerch[merchId][productName][currentDateRange] = data;
      return Promise.resolve(this.cartDataByMerch[merchId][productName][currentDateRange]);
    });
  }

  // Totals for all merchants
  queryCartSummary(pubId, params) {
    const endpoint = `/api/v0/publishers/${pubId}/bestsellers/totals/`;
    return this.Api.get(endpoint, params).then(resp => {
      const data = resp.data.data.length ? resp.data.data[0] : {};
      return Promise.resolve(data);
    });
  }

  /* Formatting functions */
  displayPrice(product, type) {
    const price =
      type === 'sale' ? product.sale_price : product.feed_price_usd || product.unit_price_usd;
    return formatValue(price, 'currency');
  }

  getProductLabel(product) {
    let brand = product.product_brand;
    let name = product.product_name;

    // Title case all brand and product names
    if (brand === 'NULL' || !brand) {
      brand = null;
    } else {
      brand = titleCase(brand);
    }
    name = name ? titleCase(name) : '';

    return brand ? `${brand} | ${name}` : name;
  }
}

export default new ProductService();
